<template>
  <redesign-layout minimal>
    <div class="rd-forgot-shell">
      <section class="rd-card rd-auth-card rd-forgot-card">
        <template v-if="requestSent">
          <div class="rd-forgot-state">
            <div class="rd-forgot-state-icon is-success">
              <i class="fa fa-paper-plane" aria-hidden="true"></i>
            </div>
            <span class="rd-kicker">Permintaan Diproses</span>
            <h1>Permintaan Telah Diproses</h1>
            <p class="rd-subtitle">{{ successMessage }}</p>
            <div class="rd-forgot-actions">
              <router-link
                to="/login"
                class="rd-button rd-button--primary rd-button--block"
              >
                <i class="fa fa-sign-in" aria-hidden="true"></i>
                Kembali ke Login
              </router-link>
              <button
                type="button"
                class="rd-button rd-button--soft rd-button--block"
                @click="resetRequest"
              >
                Kirim Permintaan Lain
              </button>
            </div>
          </div>
        </template>

        <template v-else>
          <span class="rd-kicker">Pemulihan Akun</span>
          <h1>Lupa Password?</h1>
          <p class="rd-subtitle">
            Masukkan email akun Anda. Jika email terdaftar, sistem akan
            melanjutkan proses pemulihan akun melalui kanal yang tersedia.
          </p>

          <form novalidate @submit.prevent="requestForgot">
            <div class="rd-field">
              <label class="rd-label" for="rd-forgot-email">Email Akun</label>
              <input
                id="rd-forgot-email"
                v-model="fields.username"
                class="rd-input"
                :class="{ 'is-invalid': validation.username }"
                type="email"
                inputmode="email"
                autocomplete="email"
                placeholder="nama@email.com"
                :disabled="isLoading"
                @input="clearFieldError"
              />
              <div v-if="validation.username" class="rd-form-error">
                {{ validation.username }}
              </div>
            </div>

            <div v-if="errorMessage" class="rd-alert" role="alert">
              <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
              <span>{{ errorMessage }}</span>
            </div>

            <button
              type="submit"
              class="rd-button rd-button--primary rd-button--block"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="rd-spinner" aria-hidden="true"></span>
              <i v-else class="fa fa-key" aria-hidden="true"></i>
              {{ isLoading ? "Memproses..." : "Kirim Permintaan" }}
            </button>

            <p class="rd-auth-switch">
              Ingat password Anda?
              <router-link to="/login" class="rd-link">Masuk</router-link>
            </p>
            <p class="rd-auth-agent">
              Belum punya akun?
              <router-link to="/subscriptions" class="rd-link">Daftar</router-link>
            </p>
          </form>
        </template>
      </section>
    </div>
  </redesign-layout>
</template>

<script>
import RedesignLayout from "../redesign/components/RedesignLayout.vue";
import { getRecaptchaToken } from "@/utils/recaptcha";
import { getApiErrorMessage } from "@/utils/redesignErrors";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "forgot",
  components: {
    RedesignLayout
  },
  data() {
    return {
      fields: {
        username: "",
        token: ""
      },
      validation: {
        username: ""
      },
      isLoading: false,
      errorMessage: "",
      successMessage: "",
      requestSent: false,
      recaptchaRefreshing: false,
      recaptchaTimer: null
    };
  },
  created() {
    this.refreshRecaptcha();
    this.recaptchaTimer = setInterval(() => {
      this.refreshRecaptcha();
    }, 90000);
  },
  beforeDestroy() {
    if (this.recaptchaTimer) {
      clearInterval(this.recaptchaTimer);
      this.recaptchaTimer = null;
    }
  },
  methods: {
    clearFieldError() {
      this.validation.username = "";
    },
    validateForm() {
      const username = String(this.fields.username || "").trim();
      if (!username) {
        this.validation.username = "Email akun wajib diisi.";
        return false;
      }
      if (!EMAIL_PATTERN.test(username)) {
        this.validation.username = "Masukkan alamat email yang valid.";
        return false;
      }
      this.validation.username = "";
      return true;
    },
    async refreshRecaptcha() {
      if (this.recaptchaRefreshing) {
        return this.fields.token;
      }
      this.recaptchaRefreshing = true;
      try {
        this.fields.token = await getRecaptchaToken(
          this,
          "forgotpassword",
          15000
        );
        return this.fields.token;
      } catch (error) {
        this.fields.token = "";
        return "";
      } finally {
        this.recaptchaRefreshing = false;
      }
    },
    async requestForgot() {
      if (this.isLoading) {
        return;
      }
      this.errorMessage = "";
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;
      try {
        this.fields.username = this.fields.username.trim();
        if (!this.fields.token) {
          await this.refreshRecaptcha();
        }
        if (!this.fields.token) {
          this.errorMessage =
            "Permintaan pemulihan belum dapat diproses. Silakan coba lagi.";
          return;
        }

        const response = await this.$store.dispatch(
          "forgot/requestForgot",
          this.fields
        );
        if (!response || Number(response.status) !== 200) {
          this.errorMessage = this.getRequestErrorMessage(response);
          await this.refreshRecaptcha();
          return;
        }

        const apiMessage =
          response.msg && String(response.msg).trim()
            ? String(response.msg).trim()
            : "";
        this.successMessage = this.isGenericSuccessMessage(apiMessage)
          ? "Permintaan pemulihan akun berhasil diproses. Silakan lanjutkan sesuai instruksi yang tersedia untuk akun Anda."
          : apiMessage;
        this.requestSent = true;
        this.fields.username = "";
        this.hideRecaptchaBadge();
      } catch (error) {
        this.errorMessage = this.getRequestErrorMessage(error);
        await this.refreshRecaptcha();
      } finally {
        this.isLoading = false;
      }
    },
    isGenericSuccessMessage(message) {
      return !message || /^(ok|success|sukses)\.?$/i.test(message);
    },
    getRequestErrorMessage(error) {
      if (error && error.response) {
        const status = Number(error.response.status);
        if (status === 401 || status === 403) {
          return "Permintaan pemulihan tidak dapat diproses untuk akun ini.";
        }
        if (status >= 500) {
          return "Layanan pemulihan sedang tidak tersedia. Silakan coba lagi.";
        }
      }
      return getApiErrorMessage(
        error,
        "Permintaan pemulihan belum berhasil. Periksa koneksi Anda lalu coba lagi."
      );
    },
    resetRequest() {
      this.requestSent = false;
      this.successMessage = "";
      this.errorMessage = "";
      this.fields.username = "";
      this.fields.token = "";
      this.validation.username = "";
      this.refreshRecaptcha();
    },
    hideRecaptchaBadge() {
      Promise.resolve(this.$recaptchaInstance)
        .then(function(instance) {
          if (instance && typeof instance.hideBadge === "function") {
            instance.hideBadge();
          }
        })
        .catch(function() {});
    }
  }
};
</script>

<style scoped>
.rd-forgot-shell {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}

.rd-forgot-card {
  padding: 38px;
}

.rd-forgot-card .rd-alert {
  margin-bottom: 14px;
}

.rd-forgot-state {
  text-align: center;
}

.rd-forgot-state .rd-kicker {
  justify-content: center;
}

.rd-forgot-state .rd-subtitle {
  margin-bottom: 22px;
}

.rd-forgot-state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 14px;
  border-radius: 50%;
  font-size: 22px;
}

.rd-forgot-state-icon.is-success {
  background: #ecfdf3;
  color: #15803d;
}

.rd-forgot-state-icon.is-danger {
  background: #fef2f2;
  color: #dc2626;
}

.rd-forgot-actions {
  display: grid;
  gap: 9px;
  margin-top: 4px;
}

.rd-forgot-help {
  margin: 13px 0 0;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.6;
  text-align: center;
}

@media (max-width: 575.98px) {
  .rd-forgot-card {
    padding: 24px 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/forgot/index.vue